import React, { useState, useEffect } from 'react';
import { HomeIcon, InformationCircleIcon, WrenchScrewdriverIcon, AcademicCapIcon, EnvelopeIcon } from '@heroicons/react/24/outline';

const Navbar = () => {
  const [isAtTop, setIsAtTop] = useState(true); // Removed unused isOpen state

  const navItems = [
    { 
      name: 'HOME', 
      href: '#home',
      icon: HomeIcon,
      mobileLabel: 'Home'
    },
    { 
      name: 'ABOUT US', 
      href: '#about',
      icon: InformationCircleIcon,
      mobileLabel: 'About'
    },
    { 
      name: 'SERVICES', 
      href: '#services',
      icon: WrenchScrewdriverIcon,
      mobileLabel: 'Services'
    },
    { 
      name: 'OUR TRAINING', 
      href: '#training',
      icon: AcademicCapIcon,
      mobileLabel: 'Training'
    },
    { 
      name: 'CONTACT', 
      href: '#contact',
      icon: EnvelopeIcon,
      mobileLabel: 'Contact'
    },
  ];

  // Handle scroll events
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      setIsAtTop(scrollTop < 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* Mobile Logo Header (Top) */}
      <header 
        className={`fixed w-full z-50 transition-all duration-300 md:hidden ${
          isAtTop 
            ? 'bg-transparent text-[#fff8f3]' 
            : 'bg-[#fff8f3] shadow-md text-gray-900'
        }`}
      >
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center h-16">
            {/* Logo and Company Name */}
            <div className="flex-shrink-0">
              <a href="#home" className="flex items-center">
                <img 
                  src="/gxti-svg.svg" 
                  alt="GxT International Logo"
                  className={`h-8 w-auto transition-all duration-300 ${
                    isAtTop ? 'invert brightness-0' : ''
                  }`}
                />
                <span className={`ml-2 text-lg font-bold transition-colors duration-300 ${
                  isAtTop ? 'text-[#fff8f3]' : 'text-gray-900'
                }`}>
                  {/* GxT International */}
                </span>
              </a>
            </div>

            {/* Optional: Add a call-to-action button or contact info */}
            {/* <div className={`transition-colors duration-300 ${
              isAtTop ? 'text-[#fff8f3]/80' : 'text-gray-600'
            }`}>
              <a 
                href="tel:+2347064923306" 
                className="hidden sm:flex items-center text-sm hover:text-blue-600 transition-colors duration-300"
              >
                <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                Call Us
              </a>
            </div> */}
          </div>
        </div>
      </header>

      {/* Desktop Navigation - Top */}
      <header 
        className={`fixed w-full z-50 transition-all duration-300 hidden md:block ${
          isAtTop 
            ? 'bg-transparent text-[#fff8f3]' 
            : 'bg-[#fff8f3] shadow-md text-gray-900'
        }`}
      >
        <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex-shrink-0">
              <a href="#home" className="flex items-center">
                <img 
                  src="/gxti-svg.svg" 
                  alt="GxT International Logo"
                  className={`h-10 w-auto transition-all duration-300 ${
                    isAtTop ? 'invert brightness-0' : ''
                  }`}
                />
                <span className={`ml-3 text-xl font-bold hidden lg:block transition-colors duration-300 ${
                  isAtTop ? 'text-[#fff8f3]' : 'text-gray-900'
                }`}>
                  {/* GxT International */}
                </span>
              </a>
            </div>

            {/* Desktop Navigation Links */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8">
                {navItems.map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    className={`px-3 py-2 text-sm font-medium transition-colors duration-300 hover:text-blue-600 ${
                      isAtTop 
                        ? 'text-[#fff8f3] hover:text-blue-300' 
                        : 'text-gray-700 hover:text-blue-600'
                    }`}
                  >
                    {item.name}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </nav>
      </header>

      {/* Mobile Navigation - Bottom */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 md:hidden">
        {/* Background with blur and adaptive color */}
        <div className={`absolute inset-0 transition-all duration-300 ${
          isAtTop 
            ? 'bg-[#fff8f3]/10 backdrop-blur-md' 
            : 'bg-[#fff8f3]/90 backdrop-blur-md'
        }`}></div>
        
        {/* Border with adaptive color */}
        <div className={`absolute top-0 left-0 right-0 h-[3px] transition-all duration-300 ${
          isAtTop 
            ? 'bg-gradient-to-r from-blue-300/50 via-blue-400/50 to-blue-500/50' 
            : 'bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 opacity-30'
        }`}></div>
        
        {/* Navigation Items */}
        <div className="relative flex justify-around items-center h-16 px-4">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <a
                key={item.name}
                href={item.href}
                className="flex flex-col items-center justify-center flex-1 group"
              >
                <div className="relative">
                  <Icon className={`h-6 w-6 transition-colors duration-300 group-hover:text-blue-600 ${
                    isAtTop ? 'text-[#fff8f3]/90' : 'text-gray-600'
                  }`} />
                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-blue-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <span className={`text-xs mt-1 transition-colors duration-300 group-hover:text-blue-600 ${
                  isAtTop ? 'text-[#fff8f3]/90' : 'text-gray-600'
                }`}>
                  {item.mobileLabel}
                </span>
              </a>
            );
          })}
        </div>
      </nav>

      {/* Spacer for mobile header - prevents content from being hidden behind fixed header */}
      {/* <div className="h-16 md:hidden"></div> */}
    </>
  );
};

export default Navbar;